/**
 * Inventory.js — Inventário do Pátio (Onda 46)
 * CI Sistemas de Informática Ltda. — FastALPR Parking
 *
 * Vistoria de campo, feita andando pelo pátio com o celular na mão. Cada card é
 * um veículo que o sistema acredita estar lá; um toque confirma que está. O que
 * não for confirmado é encerrado sem cobrança ao finalizar.
 *
 * Três decisões de desenho vêm do uso real:
 *  - O que já foi marcado fica guardado no aparelho antes de subir. Pátio tem
 *    sombra de sinal, e perder meia vistoria por causa disso seria inaceitável.
 *  - A busca por placa está no topo e sempre visível: com 180 cards, digitar
 *    três letras é mais rápido que rolar a lista.
 *  - Finalizar mostra o que vai acontecer antes de acontecer, com o número de
 *    veículos que serão encerrados.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FiCheck, FiSearch, FiX, FiPlay, FiFlag, FiRotateCcw, FiAlertTriangle,
  FiClock, FiPlusCircle, FiWifiOff, FiCamera,
} from 'react-icons/fi';
import toast from 'react-hot-toast';
import api from '../services/api';
import '../styles/Inventory.css';

const CHAVE_LOCAL = 'fastalpr_inventario_pendente';

const duracao = (min) => {
  if (min === null || min === undefined) return '—';
  const h = Math.floor(min / 60);
  if (h < 1) return `${min} min`;
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
};

// Onda 118: o veículo do retrato cujo ticket já não está ativo SAIU do sistema depois
// que a vistoria foi aberta — pago, baixado pela Janela de Abandono ou cancelado. Não
// há o que conferir nele, e a finalização não o encerra. Em 29/09/2026 o G2 mostrava
// "0 de 80 conferidos" numa vistoria aberta em 18/09, com os 80 já fora do sistema.
export const jaSaiu = (i) => i.status === 'saiu_durante' || (i.status === 'pendente' && i.ticket_status !== 'ativo');
const MOTIVO_DA_SAIDA = {
  pago: 'pago', pago_automatico: 'pago', isento: 'isento', finalizado: 'baixado',
  cancelado: 'cancelado', abandonado: 'baixado',
};
export const motivoDaSaida = (i) => MOTIVO_DA_SAIDA[i.ticket_status] || (i.ticket_status ? i.ticket_status : 'ticket removido');
// Vistoria aberta há mais que isto é tratada como esquecida: a tela diz de quando é o retrato.
export const HORAS_VISTORIA_ANTIGA = 12;

const horaCurta = (dt) => (dt ? new Date(dt).toLocaleString('pt-BR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
}) : '—');

function evidenceUrl(caminho) {
  if (!caminho) return null;
  const token = localStorage.getItem('token');
  return `/api/evidence/${caminho}?token=${encodeURIComponent(token)}`;
}

/** Marcações que ainda não subiram, guardadas por sessão. */
function lerFila(sessionId) {
  try {
    const bruto = JSON.parse(localStorage.getItem(CHAVE_LOCAL) || '{}');
    return bruto[sessionId] || {};
  } catch (_) {
    return {};
  }
}

function gravarFila(sessionId, fila) {
  try {
    const bruto = JSON.parse(localStorage.getItem(CHAVE_LOCAL) || '{}');
    if (Object.keys(fila).length === 0) delete bruto[sessionId];
    else bruto[sessionId] = fila;
    localStorage.setItem(CHAVE_LOCAL, JSON.stringify(bruto));
  } catch (_) {
    // Navegador sem armazenamento: a vistoria segue, sem a rede de segurança.
  }
}

export default function Inventory() {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('pendentes');
  const [confirmando, setConfirmando] = useState(false);
  const [achado, setAchado] = useState('');
  const [mostrarAchado, setMostrarAchado] = useState(false);
  const [historico, setHistorico] = useState(null);
  const [offline, setOffline] = useState(false);
  const filaRef = useRef({});

  const sessao = dados?.session || null;
  const itens = useMemo(() => dados?.items || [], [dados]);
  const resumo = dados?.summary || null;

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/api/inventory/current');
      setDados(r.data);
      if (r.data.session) filaRef.current = lerFila(r.data.session.id);
      setOffline(false);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível carregar o inventário');
    } finally {
      setCarregando(false);
    }
  }, []);

  const carregarHistorico = useCallback(async () => {
    try {
      const r = await api.get('/api/inventory/history?limit=10');
      setHistorico(r.data);
    } catch (_) { setHistorico(null); }
  }, []);

  useEffect(() => { carregar(); carregarHistorico(); }, [carregar, carregarHistorico]);

  /** Sobe o que estiver na fila. Chamado a cada marcação e ao voltar a rede. */
  const sincronizar = useCallback(async (sessionId) => {
    const fila = filaRef.current;
    const pendentes = Object.entries(fila);
    if (!sessionId || pendentes.length === 0) return;
    try {
      const r = await api.post(`/api/inventory/${sessionId}/check`, {
        items: pendentes.map(([ticket_id, found]) => ({ ticket_id: Number(ticket_id), found })),
      });
      filaRef.current = {};
      gravarFila(sessionId, {});
      setOffline(false);
      setDados(d => (d ? { ...d, summary: r.data.summary } : d));
    } catch (_) {
      // Fica na fila e no armazenamento local até a próxima tentativa.
      setOffline(true);
    }
  }, []);

  useEffect(() => {
    if (!sessao) return undefined;
    const t = setInterval(() => sincronizar(sessao.id), 15000);
    const aoVoltar = () => sincronizar(sessao.id);
    window.addEventListener('online', aoVoltar);
    return () => { clearInterval(t); window.removeEventListener('online', aoVoltar); };
  }, [sessao, sincronizar]);

  const alternar = (item) => {
    if (!sessao) return;
    const encontrado = item.status !== 'encontrado';
    // A tela responde na hora; a rede vem atrás. Numa vistoria a pé, esperar o
    // servidor a cada toque tornaria o trabalho penoso.
    setDados(d => ({
      ...d,
      items: d.items.map(i => (i.ticket_id === item.ticket_id
        ? { ...i, status: encontrado ? 'encontrado' : 'pendente' } : i)),
    }));
    filaRef.current = { ...filaRef.current, [item.ticket_id]: encontrado };
    gravarFila(sessao.id, filaRef.current);
    sincronizar(sessao.id);
  };

  const iniciar = async () => {
    setCarregando(true);
    try {
      const r = await api.post('/api/inventory/start');
      setDados(r.data);
      filaRef.current = {};
      toast.success(`Vistoria aberta com ${r.data.session.snapshot_count} veículo(s)`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível iniciar');
    } finally {
      setCarregando(false);
    }
  };

  const finalizar = async () => {
    try {
      await sincronizar(sessao.id);
      const r = await api.post(`/api/inventory/${sessao.id}/finish`);
      toast.success(`${r.data.closed} veículo(s) encerrado(s) sem cobrança`);
      setConfirmando(false);
      gravarFila(sessao.id, {});
      await carregar();
      await carregarHistorico();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível finalizar');
    }
  };

  const cancelar = async () => {
    if (!window.confirm('Descartar esta vistoria? Nenhum ticket será encerrado.')) return;
    try {
      await api.post(`/api/inventory/${sessao.id}/cancel`);
      gravarFila(sessao.id, {});
      toast.success('Vistoria descartada');
      await carregar();
      await carregarHistorico();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível cancelar');
    }
  };

  const reverter = async (id) => {
    if (!window.confirm('Reabrir todos os veículos encerrados por esta vistoria?')) return;
    try {
      const r = await api.post(`/api/inventory/${id}/revert`);
      toast.success(`${r.data.reopened} ticket(s) reaberto(s)`);
      await carregarHistorico();
      await carregar();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível reverter');
    }
  };

  const registrarAchado = async () => {
    const placa = achado.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (placa.length < 6) { toast.error('Informe a placa completa'); return; }
    try {
      const r = await api.post(`/api/inventory/${sessao.id}/finding`, { plate: placa });
      setDados(d => ({ ...d, findings: r.data.findings }));
      setAchado('');
      setMostrarAchado(false);
      toast.success(`${placa} registrado para o gestor`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível registrar');
    }
  };

  const visiveis = useMemo(() => {
    const termo = busca.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return itens.filter(i => {
      if (termo && !i.plate.includes(termo)) return false;
      if (filtro === 'pendentes') return i.status === 'pendente' && !jaSaiu(i);
      if (filtro === 'encontrados') return i.status === 'encontrado';
      if (filtro === 'sairam') return jaSaiu(i);
      return true;
    });
  }, [itens, busca, filtro]);

  if (carregando && !dados) {
    return <div className="loading"><div className="spinner" /><p>Carregando...</p></div>;
  }

  // ── Sem vistoria aberta ───────────────────────────────────────────────────
  if (!sessao) {
    return (
      <div className="inv-page">
        <div className="page-header">
          <div>
            <h1>Inventário do Pátio</h1>
            <p className="page-subtitle">
              Vistoria de campo: confira os veículos que estão de fato no pátio. Os que não forem
              encontrados são encerrados sem cobrança, com registro de quem vistoriou.
            </p>
          </div>
        </div>

        <div className="inv-start">
          <span className="inv-start-num">{dados?.yard_now ?? 0}</span>
          <span className="inv-start-label">
            veículo{dados?.yard_now === 1 ? '' : 's'} com ticket em aberto
          </span>
          <button className="btn btn-primary btn-large" onClick={iniciar} disabled={carregando || !dados?.yard_now}>
            <FiPlay /> Iniciar vistoria
          </button>
          <p className="inv-start-hint">
            A lista é fechada no momento em que você começa. Veículos que entrarem durante a vistoria
            não entram nela e não serão encerrados.
          </p>
        </div>

        {historico?.sessions?.length > 0 && (
          <section className="inv-hist">
            <h2>Vistorias anteriores</h2>
            <div className="inv-hist-list">
              {historico.sessions.map(s => (
                <article key={s.id} className={`inv-hist-item status-${s.status}`}>
                  <div className="inv-hist-head">
                    <strong>Nº {s.id}</strong>
                    <span className={`badge inv-status-${s.status}`}>{s.status.replace('_', ' ')}</span>
                  </div>
                  <p className="inv-hist-when">
                    {horaCurta(s.started_at)} · {s.started_by}
                    {s.duration_minutes ? ` · ${duracao(s.duration_minutes)}` : ''}
                  </p>
                  <p className="inv-hist-nums">
                    <span><strong>{s.snapshot_count}</strong> no pátio</span>
                    <span><strong>{s.checked_count}</strong> encontrados</span>
                    <span><strong>{s.closed_count}</strong> encerrados</span>
                    {s.findings_count > 0 && <span><strong>{s.findings_count}</strong> sem ticket</span>}
                  </p>
                  {s.revertable && (
                    <button className="btn btn-sm btn-secondary" onClick={() => reverter(s.id)}>
                      <FiRotateCcw /> Reverter
                    </button>
                  )}
                </article>
              ))}
            </div>
            <p className="report-note">
              Uma vistoria finalizada pode ser revertida por {historico.revert_window_hours}h, reabrindo
              todos os tickets que ela encerrou. Depois disso fica selada.
            </p>
          </section>
        )}
      </div>
    );
  }

  // ── Vistoria em andamento ─────────────────────────────────────────────────
  const pct = resumo?.pct_conferido ?? 0;
  const abaixoDoAviso = pct < (dados?.warn_below_pct ?? 60);
  const aConferirTotal = resumo?.a_conferir_total ?? resumo?.total ?? 0;
  const sairam = resumo?.saiu_durante ?? 0;
  const horasAberta = sessao.started_at ? (Date.now() - new Date(sessao.started_at).getTime()) / 3600000 : 0;
  const antiga = horasAberta >= HORAS_VISTORIA_ANTIGA;

  return (
    <div className="inv-page inv-running">
      {antiga && (
        <div className="inv-antiga" role="alert">
          <FiAlertTriangle />
          <div>
            <strong>Esta vistoria foi aberta em {horaCurta(sessao.started_at)} por {sessao.started_by}.</strong>{' '}
            A lista é o retrato do pátio naquele momento, não o de agora
            {sairam > 0 ? <> — <strong>{sairam}</strong> dos {resumo?.total ?? 0} veículos já saíram do sistema desde então</> : ''}.
            {' '}Se ela não está mais em uso, descarte-a: nenhum ticket é alterado, e a tela volta a mostrar o pátio atual.
          </div>
          <button className="btn btn-sm btn-secondary" onClick={cancelar}>Descartar vistoria</button>
        </div>
      )}
      <div className="inv-bar">
        <div className="inv-progress">
          <div className="inv-progress-nums">
            <strong>{resumo?.encontrados ?? 0}</strong>
            <span>de {aConferirTotal} conferidos</span>
            {sairam > 0 && <span className="inv-sairam-nota">· {sairam} já saíram</span>}
            {offline && (
              <span className="inv-offline" title="Sem conexão: as marcações estão guardadas no aparelho">
                <FiWifiOff /> sem rede
              </span>
            )}
          </div>
          <div className="inv-progress-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <span style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="inv-search">
          <FiSearch />
          <input
            type="search"
            inputMode="latin"
            placeholder="Buscar placa"
            value={busca}
            onChange={e => setBusca(e.target.value)}
            aria-label="Buscar placa"
          />
          {busca && <button onClick={() => setBusca('')} aria-label="Limpar busca"><FiX /></button>}
        </div>

        <div className="inv-filtros">
          {[['pendentes', 'A conferir'], ['encontrados', 'Conferidos'], ['sairam', 'Já saíram'], ['todos', 'Todos']]
            .filter(([id]) => id !== 'sairam' || sairam > 0)
            .map(([id, rotulo]) => (
            <button key={id} className={`btn btn-sm ${filtro === id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFiltro(id)}>
              {rotulo}
              {id === 'pendentes' && ` (${resumo?.pendentes ?? 0})`}
              {id === 'encontrados' && ` (${resumo?.encontrados ?? 0})`}
              {id === 'sairam' && ` (${sairam})`}
            </button>
          ))}
        </div>
      </div>

      {visiveis.length === 0 ? (
        <p className="no-data">
          {busca ? `Nenhuma placa com "${busca}"` : 'Nada aqui — troque o filtro acima'}
        </p>
      ) : (
        <div className="inv-grid">
          {visiveis.map(item => {
            const marcado = item.status === 'encontrado';
            const saiu = jaSaiu(item);
            const foto = evidenceUrl(item.entry_image_path);
            return (
              <button
                key={item.ticket_id}
                type="button"
                className={`inv-card ${marcado ? 'marcado' : ''} ${saiu ? 'saiu' : ''}`}
                onClick={() => !saiu && alternar(item)}
                disabled={saiu}
                aria-pressed={marcado}
                title={saiu ? `O ticket deste veículo já foi encerrado (${motivoDaSaida(item)}): não há o que conferir` : undefined}
              >
                <span className="inv-card-check" aria-hidden="true">{marcado && <FiCheck />}</span>
                <span className="inv-card-placa">{item.plate}</span>
                {saiu
                  ? <span className="inv-card-tempo inv-card-saiu">Já saiu · {motivoDaSaida(item)}</span>
                  : <span className="inv-card-tempo"><FiClock /> {duracao(item.minutes_in_yard)}</span>}
                {foto
                  ? <img className="inv-card-foto" src={foto} alt="" loading="lazy" />
                  : <span className="inv-card-semfoto"><FiCamera /></span>}
              </button>
            );
          })}
        </div>
      )}

      {dados.findings?.length > 0 && (
        <section className="inv-achados">
          <h3>Encontrados sem ticket ({dados.findings.length})</h3>
          <ul>
            {dados.findings.map(f => (
              <li key={f.id}><strong>{f.plate}</strong> {f.notes ? `— ${f.notes}` : ''}</li>
            ))}
          </ul>
        </section>
      )}

      <div className="inv-rodape">
        <button className="btn btn-secondary" onClick={() => setMostrarAchado(true)}>
          <FiPlusCircle /> Sem ticket
        </button>
        <button className="btn btn-secondary" onClick={cancelar}>Descartar</button>
        <button className="btn btn-primary" onClick={() => setConfirmando(true)}>
          <FiFlag /> Finalizar
        </button>
      </div>

      {mostrarAchado && (
        <div className="inv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setMostrarAchado(false); }}>
          <div className="inv-modal">
            <h3>Veículo sem ticket</h3>
            <p>
              Encontrou um carro no pátio que não está na lista? A placa vira uma pendência para o
              gestor. Nenhum ticket é criado: não há como saber a que horas ele entrou.
            </p>
            <input
              className="inv-modal-input"
              value={achado}
              onChange={e => setAchado(e.target.value.toUpperCase())}
              placeholder="ABC1D23"
              maxLength={8}
              autoFocus
            />
            <div className="inv-modal-acoes">
              <button className="btn btn-secondary" onClick={() => setMostrarAchado(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={registrarAchado}>Registrar</button>
            </div>
          </div>
        </div>
      )}

      {confirmando && (
        <div className="inv-modal-overlay" onClick={e => { if (e.target === e.currentTarget) setConfirmando(false); }}>
          <div className="inv-modal">
            <h3>Finalizar a vistoria</h3>

            {abaixoDoAviso && (
              <p className="inv-alerta">
                <FiAlertTriangle /> Você conferiu <strong>{resumo.encontrados} de {resumo.total}</strong> ({pct}%).
                Se a vistoria não estiver completa, veículos que estão no pátio serão encerrados.
              </p>
            )}

            <p>
              <strong>{resumo?.pendentes ?? 0}</strong> veículo(s) não foram encontrados e serão
              encerrados <strong>sem cobrança</strong>. Eles saíram sem passar pelo caixa, então não há
              tarifa a registrar.
            </p>
            {sairam > 0 && (
              <p className="inv-modal-hint">
                Os <strong>{sairam}</strong> veículo(s) que já saíram do sistema desde a abertura da vistoria
                não são tocados: ficam registrados como "saíram durante a vistoria".
              </p>
            )}
            <p className="inv-modal-hint">
              Cada ticket ficará marcado com esta vistoria, e você pode reverter tudo por
              {' '}{historico?.revert_window_hours ?? 24}h.
            </p>

            <div className="inv-modal-acoes">
              <button className="btn btn-secondary" onClick={() => setConfirmando(false)}>Voltar</button>
              <button className="btn btn-danger" onClick={finalizar}>
                Encerrar {resumo?.pendentes ?? 0} veículo(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
